import { supabase } from "@/lib/supabase";

/**
 * BW19 PR-B — typed Worker calls for the capture surfaces.
 *
 * All routes require a Supabase access token bearer header. The Worker
 * verifies it with /auth/v1/user and infers user_id from the result.
 */

const WORKER_URL =
  (import.meta.env.VITE_HAUS_WORKER_URL as string | undefined) ??
  "https://haus-worker.rackljo.workers.dev";

async function bearerHeaders(): Promise<Headers> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  const h = new Headers();
  if (token) h.set("Authorization", `Bearer ${token}`);
  return h;
}

export type CaptureSource =
  | "keyboard"
  | "share_sheet"
  | "voice"
  | "photo"
  | "browser_ext"
  | "email";

export type CreateCaptureInput = {
  content: string;
  source: CaptureSource;
  source_url?: string | null;
  source_metadata?: Record<string, unknown>;
};

export async function createCapture(input: CreateCaptureInput): Promise<{ id: string }> {
  const headers = await bearerHeaders();
  headers.set("Content-Type", "application/json");
  const resp = await fetch(`${WORKER_URL}/api/capture`, {
    method: "POST",
    headers,
    body: JSON.stringify({
      ...input,
      captured_in_tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
    }),
  });
  if (!resp.ok) {
    const text = await resp.text();
    throw new Error(`capture_failed_${resp.status}: ${text.slice(0, 200)}`);
  }
  return (await resp.json()) as { id: string };
}

export async function createVoiceCapture(args: {
  audio: Blob;
  filename?: string;
  durationS?: number | null;
}): Promise<{ id: string; transcription: string }> {
  const form = new FormData();
  form.append(
    "audio",
    new File([args.audio], args.filename ?? "voice.webm", { type: args.audio.type }),
  );
  form.append("captured_in_tz", Intl.DateTimeFormat().resolvedOptions().timeZone);
  if (args.durationS != null) form.append("duration_s", String(args.durationS));

  const headers = await bearerHeaders();
  const resp = await fetch(`${WORKER_URL}/api/capture/voice`, {
    method: "POST",
    headers,
    body: form,
  });
  if (!resp.ok) {
    const text = await resp.text();
    throw new Error(`voice_capture_failed_${resp.status}: ${text.slice(0, 200)}`);
  }
  return (await resp.json()) as { id: string; transcription: string };
}

export async function createPhotoCapture(args: {
  image: Blob;
  filename?: string;
}): Promise<{ id: string; ocr_text: string }> {
  const form = new FormData();
  form.append(
    "image",
    new File([args.image], args.filename ?? "photo.jpg", { type: args.image.type }),
  );
  form.append("captured_in_tz", Intl.DateTimeFormat().resolvedOptions().timeZone);

  const headers = await bearerHeaders();
  const resp = await fetch(`${WORKER_URL}/api/capture/photo`, {
    method: "POST",
    headers,
    body: form,
  });
  if (!resp.ok) {
    const text = await resp.text();
    throw new Error(`photo_capture_failed_${resp.status}: ${text.slice(0, 200)}`);
  }
  return (await resp.json()) as { id: string; ocr_text: string };
}
